export const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8001/api').replace(/\/$/, '');

/** @deprecated use API_BASE for storage URLs; kept for callers that need origin without /api */
export const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

function isLoopbackHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  return (
    h === 'localhost'
    || h === '127.0.0.1'
    || h === '[::1]'
    || h === '::1'
    || h.endsWith('.localhost')
  );
}

/** CRA build hosts static gallery assets under /picture/ on the frontend origin. */
function getFrontendOrigin() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }
  const configured = process.env.REACT_APP_FRONTEND_URL
    || process.env.REACT_APP_URL
    || '';
  return configured.replace(/\/api\/?$/, '').replace(/\/$/, '');
}

/**
 * Seed DB paths (seed/EventGallery/…) map to bundled static files at /picture/…
 * on the React app host — works even before /api/storage is deployed on the API.
 */
function seedStaticPictureUrl(relPath) {
  const n = String(relPath || '').replace(/\\/g, '/').replace(/^\/+/, '');
  const origin = getFrontendOrigin();
  if (!origin) return null;

  const seedMap = [
    { prefix: 'seed/EventGallery/', base: '/picture/' },
    { prefix: 'seed/TeamPicture/', base: '/picture/TeamPicture/' },
    { prefix: 'seed/LogoCompetition/', base: '/picture/LogoCompetition/' },
  ];

  for (const { prefix, base } of seedMap) {
    if (n.toLowerCase().startsWith(prefix.toLowerCase())) {
      return `${origin}${base}${n.slice(prefix.length)}`;
    }
  }

  return null;
}

/**
 * Extract the relative path inside Laravel's public disk from various stored formats.
 */
function extractStorageRelPath(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;

  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      const apiStorage = u.pathname.match(/\/api\/storage\/(.+)$/i);
      if (apiStorage) return apiStorage[1];
      const publicStorage = u.pathname.match(/\/storage\/(.+)$/i);
      if (publicStorage) return publicStorage[1];
      return null;
    } catch {
      return null;
    }
  }

  const normalized = s.replace(/\\/g, '/').replace(/^\/+/, '');
  if (normalized.startsWith('storage/')) return normalized.slice('storage/'.length);
  if (normalized.startsWith('api/storage/')) return normalized.slice('api/storage/'.length);
  return normalized;
}

/**
 * Build a URL for Laravel public-disk files.
 * - Seed gallery assets → static /picture/ on the frontend host
 * - Uploads (profile, company, gallery uploads) → /api/storage/ on the API host
 */
export function getStorageUrlCandidates(path) {
  if (!path) return [];

  const raw = String(path).trim();
  if (!raw) return [];
  if (raw.startsWith('data:') || raw.startsWith('blob:')) return [raw];

  const relPath = extractStorageRelPath(raw);
  if (relPath === null) {
    return /^https?:\/\//i.test(raw) ? [raw] : [];
  }

  const seedUrl = seedStaticPictureUrl(relPath);
  if (seedUrl) return [seedUrl];

  // Prefer /api/storage/ — production API serves public disk files there.
  return [...new Set([
    `${API_BASE}/storage/${relPath}`,
    `${API_ORIGIN}/storage/${relPath}`,
  ])];
}

export const toStorageUrl = (path) => {
  const candidates = getStorageUrlCandidates(path);
  return candidates[0] || '';
};

export const pathLooksPdf = (path) => /\.pdf$/i.test(String(path || '').trim());

/**
 * Signed Laravel preview URLs must not be host-rewritten (invalidates signature).
 * Returns true only when the URL already targets the configured API origin.
 */
export const signedPreviewUrlIsUsable = (url) => {
  if (!url || !/^https?:\/\//i.test(String(url))) return false;

  try {
    const u = new URL(String(url).trim());
    const apiBase = new URL(API_ORIGIN.startsWith('http') ? API_ORIGIN : `https://${API_ORIGIN}`);
    const usable = !isLoopbackHost(u.hostname) && u.hostname === apiBase.hostname;
    return usable;
  } catch {
    return false;
  }
};
